<template>
	<div class="event-milestones">
		<div class="event-milestones__info" v-html="infoText" />
		<div class="event-milestones__total">
			<div class="event-milestones__total-left">
				<HorizontalDecorDividerFaded />
				<div class="eventium-bg">
					<AppMainIcon
						:iconName="ResourceType.EVENTIUM"
						:iconSize="44"
					/>
				</div>
			</div>
			<div class="event-milestones__total-right">
				<ShrinkText
					:text="`${translate('event.total_produced')}:`"
					:fontSize="16"
					containerClassList="label-container"
				/>
				<AppPrice
					:amount="eventiumAccumulated"
					:currency="ResourceType.EVENTIUM"
					:resourceCheck="false"
				/>
			</div>
		</div>
		<ScrollFade>
			<template #scrollContent>
				<div v-if="milestones" class="event-milestones__content">
					<div class="event-milestones__progress">
						<div
							v-for="([id, milestone], index) in milestones"
							:key="id"
							class="event-milestones__progress-step"
							:class="`event-milestones__progress-step--${milestone?.status ?? MilestoneStatus.LOCKED}`"
							@click="
								handleMilestoneProgressStepClick(id, milestone)
							"
						>
							<div
								v-if="milestone.status"
								class="progress-step__threshold-label"
							>
								<ShrinkText
									:text="formatNumber(milestone.requiredSum)"
									:fontSize="16"
									containerClassList="threshold-container"
									textClassList="threshold"
								/>
								<UniDirectionalLabelBackground
									:skin="
										getUniDirectionalLabelBackgroundSkin(
											milestone.status,
										)
									"
								/>
								<div class="arrow arrow--right-18" />
							</div>
							<div class="progress-step__reward">
								<Tooltip
									v-if="
										milestone.reward &&
										(
											milestone.reward as MilestoneInGameRewardCurrency
										)?.amount &&
										(
											milestone.reward as MilestoneInGameRewardCurrency
										)?.currency
									"
									:disabled="
										milestone.status ===
										MilestoneStatus.CLAIMABLE
									"
									:placement="Placements.TOP"
								>
									<template #element>
										<AppPrice
											:amount="
												((): number => {
													const amount = (
														milestone.reward as MilestoneInGameRewardCurrency
													)?.amount;
													return typeof amount ===
														'function'
														? amount()
														: (amount ?? 0);
												})()
											"
											:currency="
												(
													milestone.reward as MilestoneInGameRewardCurrency
												)?.currency
											"
											:resourceCheck="false"
										/>
									</template>
									<template #tooltip>
										<span>
											{{
												translate(
													`resource.${
														(
															milestone.reward as MilestoneInGameRewardCurrency
														).currency
													}`,
												)
											}}
										</span>
									</template>
								</Tooltip>
								<Tooltip
									v-if="
										milestone.reward &&
										(
											milestone.reward as MilestoneInGameRewardBuilding
										)?.amount &&
										(
											milestone.reward as MilestoneInGameRewardBuilding
										)?.frameName
									"
									:disabled="
										milestone.status ===
										MilestoneStatus.CLAIMABLE
									"
									:heading="
										(
											milestone.reward as MilestoneInGameRewardBuilding
										).frameName ===
										FrameName.TOYMAKERS_WORKSHOP
											? translate(
													`${(milestone.reward as MilestoneInGameRewardBuilding).frameName}.name`,
												)
											: ''
									"
									customWidth="18rem"
									:placement="Placements.TOP"
								>
									<template #element>
										<AppItemAmount
											:amount="
												milestone.reward
													.amount as number
											"
											:frameName="
												(
													milestone.reward as MilestoneInGameRewardBuilding
												).frameName
											"
											:iconSize="20"
										/>
									</template>
									<template #tooltip>
										<span
											class="milestone-tooltip__content"
										>
											{{
												(
													milestone.reward as MilestoneInGameRewardBuilding
												).frameName ===
												FrameName.TOYMAKERS_WORKSHOP
													? replacePlaceholder(
															replacePlaceholder(
																replacePlaceholder(
																	translate(
																		`${FrameName.TOYMAKERS_WORKSHOP}.tooltip`,
																	),
																	"{number1}",
																	500,
																),
																"{number2}",
																100,
															),
															"{number3}",
															100,
														)
													: translate(
															`${(milestone.reward as MilestoneInGameRewardBuilding).frameName}.name`,
														)
											}}
										</span>
									</template>
								</Tooltip>
							</div>
							<div class="progress-step__icon">
								<div
									v-if="
										milestone.status ===
										MilestoneStatus.CLAIMED
									"
									class="icon-like"
								/>
								<AppNotification
									v-if="
										milestone.status ===
										MilestoneStatus.CLAIMABLE
									"
									:value="1"
									:type="StatusTypes.WARNING"
								/>
								<div
									v-if="
										milestone.status ===
										MilestoneStatus.LOCKED
									"
									class="icon-lock"
								/>
							</div>
							<div
								v-if="
									index <
									Array.from(milestones.keys()).length - 1
								"
								class="progress-step__connector"
								:class="`progress-step__connector--${milestone?.status ?? MilestoneStatus.LOCKED}`"
							/>
							<ToastMessage
								v-if="showingToast.has(id)"
								:showMessage="true"
								:message="'claimed'"
								:hasAnimation="true"
								class="milestone-toast"
							/>
						</div>
					</div>
				</div>
			</template>
		</ScrollFade>
	</div>
</template>

<script lang="ts" setup>
import AppItemAmount from "@/components/AppItemAmount.vue";
import AppMainIcon from "@/components/AppMainIcon.vue";
import AppNotification from "@/components/AppNotification.vue";
import AppPrice from "@/components/AppPrice.vue";
import ScrollFade from "@/components/ScrollFade.vue";
import ShrinkText from "@/components/ShrinkText.vue";
import HorizontalDecorDividerFaded from "@/components/SVG/HorizontalDecorDividerFaded.vue";
import UniDirectionalLabelBackground from "@/components/SVG/UniDirectionalLabelBackground.vue";
import ToastMessage from "@/components/ToastMessage.vue";
import Tooltip from "@/components/Tooltip.vue";

import type {
	MilestoneInGameRewardBuilding,
	MilestoneInGameRewardCurrency,
	MilestoneItem,
} from "@/types/event";
import { MilestoneStatus } from "@/types/event";

import {
	applyMilestoneBuilding,
	applyMilestoneCurrency,
	saveMilestoneClaim,
	useEventStore,
} from "@/event";
import { isPastEndDate } from "@/event/session/eventEndDate";
import { FrameName } from "@/game/entities/config";
import { ResourceType } from "@/game/entities/config/resources";
import { TileToastType, ToastPlacement } from "@/game/ToastMessage";
import useEmitter, { EmitterEvents } from "@/hooks/useEmitter";
import useResourcesStore from "@/store/useResourcesStore";
import {
	Placements,
	StatusTypes,
	UniDirectionalLabelSkinTypes,
} from "@/types/ui";
import { formatNumber } from "@/utils/format";
import { replacePlaceholder } from "@/utils/replacePlaceholder";
import { translate } from "@/utils/translate";
import { storeToRefs } from "pinia";
import { computed, ref } from "vue";

const resourcesStore = useResourcesStore();
const { eventiumAccumulated } = storeToRefs(resourcesStore);
const eventStore = useEventStore();
const { milestones } = storeToRefs(eventStore);
const isEventEnded = computed((): boolean => isPastEndDate(eventStore.endDate));

const showingToast = ref<Set<number>>(new Set());

const milestoneBlockAfterClick = ref<boolean>(false);

const infoText = computed((): string => {
	return translate("event.milestone_info").replace(
		"{resource}",
		`<span class='text-icon icon-${eventStore.attributes.type}-eventium-16'></span>`,
	);
});

const getUniDirectionalLabelBackgroundSkin = (
	status: MilestoneStatus,
): UniDirectionalLabelSkinTypes => {
	switch (status) {
		case MilestoneStatus.LOCKED:
			return UniDirectionalLabelSkinTypes.RED;
		case MilestoneStatus.CLAIMABLE:
			return UniDirectionalLabelSkinTypes.GOLD;
		case MilestoneStatus.CLAIMED:
			return UniDirectionalLabelSkinTypes.GREEN;
	}
};

const handleMilestoneProgressStepClick = async (
	id: number,
	milestone: MilestoneItem | null,
): Promise<void> => {
	if (isEventEnded.value) return;
	if (milestone?.status !== MilestoneStatus.CLAIMABLE) return;

	if (milestoneBlockAfterClick.value) return;
	milestoneBlockAfterClick.value = true;

	if (milestone.reward.type === "currency") {
		const reward = milestone.reward as MilestoneInGameRewardCurrency;
		if (!applyMilestoneCurrency(reward)) {
			milestoneBlockAfterClick.value = false;
			return;
		}
	} else {
		const reward = milestone.reward as MilestoneInGameRewardBuilding;
		if (!applyMilestoneBuilding(reward)) {
			milestoneBlockAfterClick.value = false;
			return;
		}
	}

	eventStore.mergeClaimedMilestonesOptimistic({ [id]: true });

	showingToast.value.add(id);
	setTimeout((): void => {
		milestoneBlockAfterClick.value = false;
	}, 250);
	setTimeout((): void => {
		showingToast.value.delete(id);
	}, 1000);

	const ok = await saveMilestoneClaim({
		eventMilestones: { [id]: true },
	});
	if (!ok) {
		useEmitter().emit(EmitterEvents.PhaserTextMessage, {
			text: translate("ui.connection_error_info"),
			type: TileToastType.WARNING,
			placement: ToastPlacement.TOP_CENTER,
		});
	}
};
</script>

<style lang="scss" scoped>
.event-milestones {
	$connectorHeight: 1rem;

	&__info {
		margin-top: 1rem;
		padding: 0 2.375rem;
		color: #fff3dd;
		font-size: 0.875rem;
		line-height: 1.25rem;
		text-align: center;
	}

	&__total {
		@include flex-row-center;
		margin-top: 1.625rem;
		margin-bottom: 1.625rem;
		padding: 0 0.625rem;
		width: 100%;

		&-left {
			position: relative;
			flex: 1 1 0;

			.eventium-bg {
				position: absolute;
				top: 50%;
				left: 1rem;
				transform: translateY(-50%);
				width: 3.75rem;
				height: 3.75rem;
				@include background(
					url($path-events + "christmas/backgrounds/eventium-bg.avif"),
					contain,
					center,
					no-repeat
				);
				@include flex-col-center;
			}
		}

		&-right {
			position: relative;
			flex: 1 1 0;
			@include flex-row-center;
			color: #f4d278;
			font-size: 1rem;

			:deep(.label-container) {
				flex: 1 1 0;
				min-width: 0;

				.v-popper {
					width: 100%;

					.shrink-text {
						display: block;
						width: 100%;
					}
				}
			}
		}

		&-left,
		&-right {
			width: 50%;
		}
	}

	&__content {
		width: 100%;
	}

	&__progress {
		@include flex-col-center;
		gap: $connectorHeight;
		padding: 0 0.625rem;

		&-step {
			position: relative;
			box-shadow:
				inset 0 0 0.5rem 0 #20150b,
				0 0 0.375rem 0.063rem rgb(189, 144, 50);
			border-width: 0.075rem;
			border-style: solid;
			border-image-source: linear-gradient(
				to top,
				rgba(189, 144, 50, 1) 35%,
				rgba(240, 211, 134, 1) 65%
			);
			border-image-slice: 1;
			background-image: linear-gradient(
				90deg,
				rgba(255, 243, 221, 1) 0%,
				rgba(242, 220, 181, 1) 100%
			);
			width: 100%;
			height: 2.5rem;
			@include flex-row-center;
			padding-right: 0.5rem;

			&::before {
				position: absolute;
				top: 0;
				left: 0;
				opacity: 1 !important;
				// NOTE: Multiply blending is better for light colors
				mix-blend-mode: multiply !important;
				background: url($path-images + "backgrounds/textures/marble-texture-bg.avif")
					repeat center;
				background-size: 40%;
				width: 100%;
				height: 100%;
				content: "";
			}

			&--claimed {
				background-image: linear-gradient(
					90deg,
					rgba(25, 179, 69, 1) 40%,
					rgba(13, 108, 39, 1) 100%
				);

				:deep(.price__value),
				:deep(.amount__value) {
					color: #fff3dd;
				}
			}

			&--claimable {
				background-image: linear-gradient(
					90deg,
					rgba(244, 210, 120, 1) 40%,
					rgba(217, 135, 28, 1) 100%
				);
			}

			&--claimable,
			&--locked {
				:deep(.price__value),
				:deep(.amount__value) {
					color: #372615;
					text-shadow: none;
				}
			}

			:deep(.milestone-toast) {
				position: absolute;
				top: -0.5rem;
				left: 50%;
				transform: translateX(-50%);
				z-index: 10;
			}

			.progress-step {
				&__threshold-label {
					position: relative;
					flex-shrink: 0;
					padding: 0 1.75rem 0 0.5rem;
					width: 6.875rem;
					height: 100%;
					@include flex-row-center;

					:deep(.threshold-container) {
						width: 100%;

						.threshold {
							position: relative;
							z-index: 1;
							color: #fff3dd;
							font-weight: 800;
							text-align: center;
							text-shadow: 0.044rem 0.044rem 0.063rem
								rgba(0, 0, 0, 0.6);
						}
					}

					:deep(.uni-directional-label-bg) {
						position: absolute;
						top: 0;
						left: 0;
						z-index: 0;
					}

					.arrow {
						position: absolute;
						right: 0.5rem;
						flex-shrink: 0;
					}
				}

				&__reward {
					position: relative;
					flex: 1 1 0;
					z-index: 1;
				}

				&__icon {
					position: relative;
					@include flex-row-center;
					z-index: 1;
					width: 1.375rem;
					height: 1.375rem;

					.icon-lock {
						width: 100%;
						height: 100%;
					}
				}

				&__connector {
					position: absolute;
					bottom: calc(($connectorHeight + 0.125rem) * -1);
					left: 2.625rem;
					z-index: 2;
					border-width: 1.2px;
					border-style: solid;
					border-image-source: linear-gradient(
						to bottom,
						#bd9032,
						#f0d386
					);
					border-image-slice: 1;
					background: #fff3dd;
					width: 0.5rem;
					height: calc($connectorHeight + 0.125rem);

					&::before {
						position: absolute;
						top: 0;
						left: 0;
						opacity: 1 !important;
						// NOTE: Multiply blending is better for light colors
						mix-blend-mode: multiply !important;
						background: url($path-images + "backgrounds/textures/marble-texture-bg.avif")
							repeat center;
						background-size: 40%;
						width: 100%;
						height: 100%;
						content: "";
					}

					&--claimed {
						background: #0d6c27;
					}
				}
			}
		}
	}
}

.milestone-tooltip__content {
	text-align: center;
}
</style>
